import React, { useMemo, useRef, useState } from 'react';
import { isIvrCall } from '../../lib/callKind';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowDownLeft, ArrowUpRight, MessageSquare, Bot, Clock, Trash2, ChevronRight,
  Play, Pause, Download, Copy, Sparkles, PhoneOutgoing, CalendarCheck, PhoneCall, Voicemail, PhoneMissed,
} from 'lucide-react';

const API_URL = window.location.origin;

const CHANNEL = {
  inbound: { icon: ArrowDownLeft, tile: 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300', node: 'bg-teal-500 shadow-[0_0_0_4px_rgba(20,184,166,0.15),0_0_14px_rgba(20,184,166,0.55)]', bar: 'bg-teal-500 dark:bg-teal-400', hex: '#14b8a6' },
  outbound: { icon: ArrowUpRight, tile: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300', node: 'bg-indigo-500 shadow-[0_0_0_4px_rgba(99,102,241,0.15),0_0_14px_rgba(99,102,241,0.55)]', bar: 'bg-indigo-500 dark:bg-indigo-400', hex: '#6366f1' },
  chat: { icon: MessageSquare, tile: 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300', node: 'bg-violet-500 shadow-[0_0_0_4px_rgba(139,92,246,0.15),0_0_14px_rgba(139,92,246,0.55)]', bar: 'bg-violet-500 dark:bg-violet-400', hex: '#8b5cf6' },
};

const CHIP = {
  booked: ['Booked', 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30', CalendarCheck],
  transferred: ['Transferred', 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30', PhoneOutgoing],
  callback: ['Callback', 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30', PhoneCall],
  voicemail: ['Voicemail', 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30', Voicemail],
  no_answer: ['No answer', 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30', PhoneMissed],
  missed: ['Missed', 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30', PhoneMissed],
  line_limit: ['Concurrent limit reached', 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30', PhoneMissed],
  answered: ['Answered', 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30', null],
  active: ['Active', 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30', null],
  closed: ['Closed', 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30', null],
};

const hashBars = (seed, n) => {
  let h = 2166136261;
  return Array.from({ length: n }, (_, i) => {
    h ^= (seed || 'x').charCodeAt(i % (seed || 'x').length) + i * 31;
    h = Math.imul(h, 16777619) >>> 0;
    return 22 + (h % 78);
  });
};

const isBooked = (entry, tag) => ['appointment_booked', 'booked'].includes(entry.outcome)
  && (entry.tags_applied || []).map(t => String(t).toLowerCase()).includes(tag);

const voiceFlags = (entry) => {
  const voicemail = entry.outcome === 'voicemail' || entry.status === 'voicemail';
  const noAnswer = entry.outcome === 'no_answer' || ['no-answer', 'busy'].includes(entry.status);
  const missed = entry.status === 'missed';
  return {
    voicemail, noAnswer, missed,
    minimal: voicemail || noAnswer || missed,
    booked: isBooked(entry, 'ai voice booked'),
    transferred: !!(entry.transfer_info?.transferred_to || entry.transfer_dial_status),
    callback: !!(entry.callback?.requested || entry.callback_requested || entry.outcome === 'callback_scheduled'),
  };
};

const voiceChip = (f, entry) => ((entry.outcome === 'concurrency_limit' || entry.concurrency_limited) ? 'line_limit'
  : f.booked ? 'booked' : f.transferred ? 'transferred' : f.callback ? 'callback'
  : f.voicemail ? 'voicemail' : f.missed ? 'missed' : f.noAnswer ? 'no_answer' : entry.status === 'completed' ? 'answered' : null);

const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
const fmtDur = (s) => (!s ? '0:00' : `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`);

export const dateGroupLabel = (d) => {
  if (!d) return 'Earlier';
  const date = new Date(d);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === y.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};

export const TimelineDateHeader = ({ label }) => (
  <div className="sticky top-0 z-10 py-2 bg-white/85 dark:bg-[#0c0d12]/85 backdrop-blur-md" data-testid={`tl-date-${label}`}>
    <span className="pl-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{label}</span>
  </div>
);

const StatusChip = ({ kind, testId }) => {
  if (!kind || !CHIP[kind]) return null;
  const [label, cls, Icon] = CHIP[kind];
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10.5px] font-semibold border whitespace-nowrap ${cls}`} data-testid={testId}>
      {Icon && <Icon size={10} />}{label}
    </span>
  );
};

const MiniWave = ({ seed, barCls }) => (
  <div className="hidden sm:flex items-center gap-[2px] h-6 px-2 rounded-md bg-slate-100 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/[0.06]" data-testid="tl-mini-wave">
    {hashBars(seed, 14).map((h, i) => (
      <span key={i} className={`w-[2px] rounded-full ${barCls} opacity-80 group-hover:opacity-100 transition-opacity`} style={{ height: `${Math.max(18, h * 0.8)}%` }} />
    ))}
  </div>
);

const DeleteControl = ({ id, confirmDelete, setConfirmDelete, onDelete, idx, prefix }) => (confirmDelete === id ? (
  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
    <button onClick={onDelete} className="px-2 py-0.5 text-[10px] font-semibold bg-red-500 text-white rounded-md hover:bg-red-600" data-testid={`confirm-delete-${prefix}-${idx}`}>Delete</button>
    <button onClick={() => setConfirmDelete(null)} className="px-2 py-0.5 text-[10px] rounded-md border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300">No</button>
  </div>
) : (
  <button onClick={e => { e.stopPropagation(); setConfirmDelete(id); }} aria-label="Delete entry"
    className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-${prefix}-${idx}`}>
    <Trash2 size={12} />
  </button>
));

const EntryShell = ({ testId, channel, isExpanded, onToggle, header, children, delay }) => (
  <motion.div className="relative pl-11" data-testid={testId}
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay, ease: 'easeOut' }}>
    <span className={`absolute left-[13px] top-[23px] w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-[#0c0d12] ${CHANNEL[channel].node}`} />
    <div className={`group rounded-xl border backdrop-blur-md transition-[border-color,box-shadow,transform] duration-200 ${isExpanded
      ? 'border-teal-500/40 bg-white dark:bg-[#0e111a] shadow-xl shadow-teal-500/5'
      : 'border-slate-200/80 dark:border-white/[0.07] bg-white/90 dark:bg-white/[0.025] hover:border-teal-500/40 hover:shadow-md hover:shadow-teal-500/5 hover:-translate-y-px'}`}>
      <div role="button" tabIndex={0} aria-expanded={isExpanded} onClick={onToggle}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()}
        className="h-[54px] px-3 flex items-center gap-3 cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
        {header}
        <ChevronRight size={14} className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
      </div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="px-4 pb-4 pt-3 border-t border-slate-200/80 dark:border-white/[0.06] space-y-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </motion.div>
);

const HeaderBody = ({ channel, title, chip, meta, right }) => {
  const Icon = CHANNEL[channel].icon;
  return (
    <>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${CHANNEL[channel].tile}`}><Icon size={15} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</span>
          {chip}
        </div>
        <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate">{meta}</div>
      </div>
      {right}
    </>
  );
};

const SectionLabel = ({ children, action }) => (
  <div className="flex items-center justify-between mb-2">
    <h5 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{children}</h5>
    {action}
  </div>
);

const SummaryHero = ({ text, facts, testId }) => (
  <div className="relative overflow-hidden rounded-xl border border-teal-500/20 bg-gradient-to-br from-teal-50 via-white to-indigo-50/60 dark:from-teal-500/[0.08] dark:via-transparent dark:to-indigo-500/[0.06] p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 mb-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-teal-700 dark:text-teal-300"><Sparkles size={11} /> AI Summary</div>
    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{text}</p>
    {facts.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{facts.map(k => <StatusChip key={k} kind={k} testId={`fact-${k}`} />)}</div>}
  </div>
);

const smoothBars = (seed, n) => {
  const raw = hashBars(seed, n);
  return raw.map((_, i) => {
    const avg = (raw[Math.max(0, i - 1)] + raw[i] * 2 + raw[Math.min(n - 1, i + 1)]) / 4;
    const envelope = 0.55 + 0.45 * Math.abs(Math.sin((i / n) * Math.PI * 3.2));
    return Math.max(14, Math.min(100, avg * envelope));
  });
};

const WavePlayer = ({ callId, subAccountId, color, api, idx }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hover, setHover] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const barCount = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches ? 48 : 100;
  const bars = useMemo(() => smoothBars(callId, barCount), [callId, barCount]);
  const progress = duration ? current / duration : 0;

  const load = async () => {
    const r = await fetch(`${API_URL}/api/sub-accounts/${subAccountId}/calls/${callId}/recording/stream`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    audioRef.current.src = URL.createObjectURL(await r.blob());
    setLoaded(true);
  };
  const toggle = async (e) => {
    e.stopPropagation();
    if (playing) { audioRef.current.pause(); setPlaying(false); return; }
    try { if (!loaded) await load(); } catch { toast.error('Failed to load recording'); return; }
    audioRef.current.play().catch(() => {});
    setPlaying(true);
  };
  const ratioAt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
  };
  const seek = (e) => {
    e.stopPropagation();
    if (duration) audioRef.current.currentTime = ratioAt(e) * duration;
  };
  const download = async (e) => {
    e.stopPropagation();
    try {
      const resp = await api.post(`/api/sub-accounts/${subAccountId}/calls/${callId}/recording/download-token`);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${API_URL}/api/sub-accounts/${subAccountId}/calls/${callId}/recording/download?token=${resp.data.download_token}`;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 30000);
    } catch { toast.error('Failed to download recording'); }
  };
  const barColor = (i) => {
    const r = (i + 0.5) / bars.length;
    if (r <= progress) return color;
    if (hover !== null && r <= hover) return `${color}80`;
    return 'rgba(148,163,184,0.38)';
  };

  return (
    <div className="flex items-center gap-2 sm:gap-3.5 min-w-0 rounded-2xl border border-slate-200/80 dark:border-white/[0.07] bg-white dark:bg-white/[0.02] pl-2.5 pr-3 py-2.5" onClick={e => e.stopPropagation()} data-testid={`tl-audio-${callId}`}>
      <audio ref={audioRef} preload="none" onEnded={() => { setPlaying(false); setCurrent(0); }}
        onTimeUpdate={() => setCurrent(audioRef.current.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current.duration || 0)} />
      <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} data-testid={`tl-play-${callId}`}
        className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-150 hover:scale-105 active:scale-95"
        style={{ background: color, color: '#ffffff', boxShadow: `0 8px 20px -8px ${color}`, }}>
        {playing && <span className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: color }} />}
        {playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
      </button>
      <div className="relative flex-1 min-w-0 h-11 flex items-center justify-between gap-[1px] cursor-pointer" onClick={seek}
        onMouseMove={e => setHover(ratioAt(e))} onMouseLeave={() => setHover(null)} data-testid={`tl-wave-${callId}`}>
        {bars.map((h, i) => (
          <span key={i} className="flex-1 min-w-0 max-w-[3px] rounded-full transition-[background-color] duration-150" style={{ height: `${h}%`, background: barColor(i) }} />
        ))}
        {(playing || current > 0) && <span className="absolute top-1 bottom-1 w-[2px] rounded-full pointer-events-none" style={{ left: `${progress * 100}%`, background: color, boxShadow: `0 0 8px ${color}` }} />}
      </div>
      <span className="hidden sm:inline text-[11px] tabular-nums font-medium text-slate-500 dark:text-slate-400 flex-shrink-0 whitespace-nowrap text-right" data-testid={`tl-time-${callId}`}>{fmtDur(current)} / {fmtDur(duration)}</span>
      <button onClick={download} className="flex-shrink-0 p-1.5 sm:p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-500/10 transition-colors" data-testid={`download-rec-${idx}`} aria-label="Download recording"><Download size={14} /></button>
    </div>
  );
};

const SPEAKER = {
  agent: { label: 'Agent', accent: '#14b8a6' },
  automation: { label: 'LeadConnector automation', accent: '#f59e0b' },
  user: { label: 'Contact', accent: null },
};

const TranscriptLine = ({ m }) => {
  if (m.role === 'system') {
    return <div className="text-center text-[11px] italic text-slate-500 dark:text-slate-400 py-0.5">{m.text}</div>;
  }
  const sp = SPEAKER[m.role] || SPEAKER.user;
  const meta = (
    <div className={`flex items-center gap-1.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 ${sp.accent ? '' : 'justify-end'}`}>
      {sp.accent && <span className="w-1.5 h-1.5 rounded-full" style={{ background: sp.accent }} />}
      {sp.label}{m.timestamp && <span className="font-normal normal-case tracking-normal">· {fmtTime(m.timestamp)}</span>}
    </div>
  );
  if (!sp.accent) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%]">
          {meta}
          <div className="px-3.5 py-2 rounded-2xl rounded-tr-md bg-slate-100 dark:bg-white/[0.06] text-[13px] leading-relaxed text-slate-800 dark:text-slate-200">{m.text}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[86%] pl-3" style={{ borderLeft: `2px solid ${sp.accent}59` }}>
      {meta}
      <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">{m.text}</p>
    </div>
  );
};

const Transcript = ({ messages, label, testId }) => (
  <div>
    <SectionLabel action={
      <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(messages.map(m => `${m.role}: ${m.text}`).join('\n')); toast.success('Copied'); }}
        className="text-[10.5px] font-medium text-slate-500 hover:text-teal-600 dark:hover:text-teal-300 flex items-center gap-1" data-testid={`copy-${testId}`}><Copy size={10} /> Copy</button>
    }>{label}</SectionLabel>
    <div className="space-y-3.5 max-h-80 overflow-y-auto pr-2" data-testid={testId}>
      {messages.map((m, i) => <TranscriptLine key={i} m={m} />)}
    </div>
  </div>
);

const TransferDetails = ({ info, idx }) => (
  <div className="rounded-xl border border-blue-200 dark:border-blue-500/20 bg-blue-50/60 dark:bg-blue-500/[0.06] p-3.5 space-y-2" data-testid={`transfer-details-${idx}`}>
    <SectionLabel>Transfer</SectionLabel>
    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{info.destination_name || 'Destination'} <span className="font-normal text-slate-500 dark:text-slate-400">· {info.transferred_to}</span></div>
    {info.whisper_briefing && <p className="text-xs italic text-slate-600 dark:text-slate-300">Whisper: "{info.whisper_briefing}"</p>}
    {info.client_message && <p className="text-xs text-slate-600 dark:text-slate-300">Caller heard: "{info.client_message}"</p>}
  </div>
);

export const VoiceEntry = ({ entry, idx, isExpanded, onToggle, subAccountId, api, onDelete, confirmDelete, setConfirmDelete }) => {
  const f = voiceFlags(entry);
  const channel = entry.direction === 'inbound' ? 'inbound' : 'outbound';
  const aiLeftVoicemail = f.voicemail && entry.voicemail_action_taken === 'left_message';
  const showAudio = entry.has_recording && (f.voicemail ? aiLeftVoicemail : !f.minimal);
  const ivr = isIvrCall(entry);
  const title = ivr ? 'IVR / Call Routing' : entry.transfer_dial_status ? `${channel === 'inbound' ? 'Inbound' : 'Outbound'} → Office` : `${channel === 'inbound' ? 'Inbound' : 'Outbound'} Call`;
  const facts = ['booked', 'callback', 'transferred', 'voicemail'].filter(k => f[k]);
  const summary = entry.summary || (f.voicemail ? (aiLeftVoicemail ? 'Agent left a voicemail.' : 'Call went to voicemail. Agent did not leave a message.')
    : f.missed ? 'Call was missed.' : f.noAnswer ? 'No answer from contact.' : '');
  return (
    <EntryShell testId={`tl-voice-${idx}`} channel={channel} isExpanded={isExpanded} onToggle={onToggle} delay={Math.min(idx, 10) * 0.035}
      header={<HeaderBody channel={channel} title={title}
        chip={<StatusChip kind={voiceChip(f, entry)} testId={`call-status-${idx}`} />}
        meta={<><span>{fmtTime(entry.created_at)}</span><span className="flex items-center gap-1"><Clock size={10} />{fmtDur(entry.duration)}</span><span className="flex items-center gap-1 truncate"><Bot size={10} />{entry.transfer_dial_status ? 'Routed to office' : (entry.agent_name || 'AI Agent')}</span>{ivr && entry.ivr_routed_to && <span className="truncate" data-testid={`tl-ivr-routed-${idx}`}>→ Routed to {entry.ivr_routed_to}</span>}</>}
        right={<>{showAudio && <MiniWave seed={entry.call_id} barCls={CHANNEL[channel].bar} />}<DeleteControl id={entry.call_id} idx={idx} prefix="entry" confirmDelete={confirmDelete} setConfirmDelete={setConfirmDelete} onDelete={onDelete} /></>}
      />}>
      {summary && <SummaryHero text={summary} facts={facts} testId={`tl-summary-${idx}`} />}
      {aiLeftVoicemail && entry.voicemail_message_text && (
        <p className="text-sm italic rounded-xl border border-purple-200 dark:border-purple-500/20 bg-purple-50/60 dark:bg-purple-500/[0.06] p-3 text-slate-700 dark:text-slate-300" data-testid={`voicemail-transcript-${idx}`}>"{entry.voicemail_message_text}"</p>
      )}
      {showAudio && <div><SectionLabel>Recording</SectionLabel><WavePlayer callId={entry.call_id} subAccountId={subAccountId} color={CHANNEL[channel].hex} api={api} idx={idx} /></div>}
      {entry.transfer_info?.transferred_to && <TransferDetails info={entry.transfer_info} idx={idx} />}
      {entry.has_transcript && !f.minimal && entry.transcript?.length > 0 && <Transcript messages={entry.transcript} label="Transcript" testId={`transcript-${idx}`} />}
    </EntryShell>
  );
};

export const ChatEntry = ({ entry, idx, isExpanded, onToggle, onDelete, confirmDelete, setConfirmDelete }) => {
  const msgCount = entry.message_count || entry.messages?.length || 0;
  const ts = entry.last_message_at || entry.timestamp || entry.created_at;
  const booked = isBooked(entry, 'ai chat booked');
  return (
    <EntryShell testId={`tl-chat-${idx}`} channel="chat" isExpanded={isExpanded} onToggle={onToggle} delay={Math.min(idx, 10) * 0.035}
      header={<HeaderBody channel="chat" title={<span data-testid={`timeline-chat-label-${idx}`}>{entry.type === 'chat-widget' ? 'Chat (Widget)' : 'Chat Session'}</span>}
        chip={<StatusChip kind={booked ? 'booked' : (entry.status === 'active' ? 'active' : 'closed')} testId={`chat-status-${idx}`} />}
        meta={<><span>{fmtTime(ts)}</span><span className="flex items-center gap-1"><MessageSquare size={10} />{msgCount} msgs</span><span className="flex items-center gap-1 truncate"><Bot size={10} />{entry.agent_name || 'AI Agent'}</span></>}
        right={<DeleteControl id={entry.session_id} idx={idx} prefix="chat" confirmDelete={confirmDelete} setConfirmDelete={setConfirmDelete} onDelete={onDelete} />}
      />}>
      {entry.summary && <SummaryHero text={entry.summary} facts={booked ? ['booked'] : []} testId={`tl-chat-summary-${idx}`} />}
      {entry.messages?.length > 0 && <Transcript messages={entry.messages} label="Conversation" testId={`conversation-${idx}`} />}
    </EntryShell>
  );
};
